import "reflect-metadata";
import { inject, injectable } from "inversify";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import TokenRepo from "./token.repo";
import ApiRepo from "./api.repo";
import { LoginModel, UserDetail } from "../models/auth/auth.model";
import axios, { AxiosError } from "axios";
import { PermissionClaim } from "../enums/permissions.enum";
import { bind } from "@react-rxjs/core";
import { factoryRuntimeSettings } from "../stores/factory-runtime-settings.store";

@injectable()
export default class AuthRepo {
  private isInitialized = false;
  private claimsRequestGeneration = 0;

  public authSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private userSubject: BehaviorSubject<UserDetail|null> = new BehaviorSubject<UserDetail|null>(null);
  private claimsSubject: Subject<PermissionClaim[]> = new BehaviorSubject<PermissionClaim[]>([]);
  
  public useAuth = bind(this.authSubject, false)[0];
  public useDetails = bind(this.userSubject, null)[0];
  public useClaims = bind(this.claimsSubject, [])[0];
  @inject(TokenRepo) private tokenRepo: TokenRepo;
  @inject(ApiRepo) private apiRepo: ApiRepo;

  async login(model: LoginModel) {
    const {userName, password, rememberPassword} = model;
    const response = await this.apiRepo.post<string>("/api/AppUser/auth", {userName, password});
    if (response?.code === "00") {
      if (rememberPassword) {
        this.tokenRepo.setToken(response.result, model.userName, model.password);
      } else {
        this.tokenRepo.setToken(response.result, model.userName);
      }
      this.clearClaims();
      void factoryRuntimeSettings.setSession(null, null);
      await this.getUserData();
      await this.getUserClaims();
      this.authSubject.next(true);
      return true;
    } else {
      return false;
    }
  }

  async getUserData(): Promise<boolean> {
    const token = this.tokenRepo.getToken();
    const userName = this.tokenRepo.getUsername();
    if (token && userName) {
      const response = await this.apiRepo.get<UserDetail>('/api/AppUser/detail', {userName});
      if (response?.code === "00") {
        if (this.tokenRepo.getToken() !== token) return false;
        await factoryRuntimeSettings.setSession(response.result.waterFactoryId, token);
        if (this.tokenRepo.getToken() !== token) return false;
        this.userSubject.next(response.result);
        return true;
      }
    }
    return false;
  }

  private clearClaims() {
    ++this.claimsRequestGeneration;
    this.claimsSubject.next([]);
  }

  async getUserClaims() {
    const generation = ++this.claimsRequestGeneration;
    const token = this.tokenRepo.getToken();
    const userName = this.tokenRepo.getUsername();
    if (token && userName) {
      const response = await this.apiRepo.get<PermissionClaim[]>('/api/AppUser/get-all-user-claim', {userName});
      if (response?.code === "00" && generation === this.claimsRequestGeneration && this.tokenRepo.getToken() === token && this.tokenRepo.getUsername() === userName) {
        this.claimsSubject.next(response.result);
      }
    }
  }

  async initializeSession(): Promise<boolean> {
    await this.registerInterceptor();
    if (!this.tokenRepo.getToken()) {
      this.authSubject.next(false);
      return false;
    }

    let authenticated = await this.getUserData();
    if (authenticated) {
      await this.getUserClaims();
      this.authSubject.next(true);
      return true;
    }

    const refreshedToken = await this.retryLoginWithLocalCreds();
    if (refreshedToken) {
      this.authSubject.next(true);
      return true;
    }

    await this.logout();
    this.authSubject.next(false);
    return false;
  }

  async logout() {
    void factoryRuntimeSettings.setSession(null, null);
    this.tokenRepo.deleteToken();
    this.tokenRepo.deleteUsername();
    this.tokenRepo.deletePassword();
    this.clearClaims();
    this.userSubject.next(null);
    this.authSubject.next(false);
  }

  async autologin() {
    const token = this.tokenRepo.getToken();
    if (token) return true;
    return false;
  }

  async retryLoginWithLocalCreds() {
    const userName = this.tokenRepo.getUsername();
    const password = this.tokenRepo.getPassword();
    if (!userName||!password) {
      await this.logout();
      return;
    };
    const response = await this.apiRepo.post<string>("/api/AppUser/auth", {userName, password});
    if (response?.code === "00") {
        this.tokenRepo.setToken(response.result, userName, password);
      this.clearClaims();
      void factoryRuntimeSettings.setSession(null, null);
      await this.getUserData();
      await this.getUserClaims();
      this.authSubject.next(true);
      return response.result;
    } else {
      await this.logout();
    }
    return;
  }

  async registerInterceptor() {
    if (this.isInitialized) return;
    axios.interceptors.response.use(undefined, async (error: AxiosError) => {
      if(error.response?.status !== 401) {
          return Promise.reject(error);
      }
      const originalRequestConfig = error.config;
      if (!originalRequestConfig) {
        return Promise.reject(error);
      }
      if (originalRequestConfig.headers["X-Retry-Attempt"]) {
        return Promise.reject(error);
      }
      const token = await this.retryLoginWithLocalCreds();
      if (!token) {
        return Promise.reject(error);
      }
      originalRequestConfig.headers.Authorization = "Bearer " + token;
      originalRequestConfig.headers["X-Retry-Attempt"] = true
      return await axios.request(originalRequestConfig);
    });
  }
}
